import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { flushSync } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { GlobalContext } from '../../providers/GlobalProvider';
import type { HtsUiPatch } from './HtsClassificationChat';
import {
  buildPatchDisplayText,
  buildConversationTitle,
  formatThoughtDuration,
  toErrorMessage,
} from './HtsClassificationChat';
import {
  attachmentHint,
  attachmentOnlyMessage,
  attachmentPreparationNote,
  attachmentSummary,
  formatFileSize,
  isSupportedImageFile,
} from './classifyAttachments';
import {
  attachmentTurnConfig,
  attachmentTurnFields,
  makeClassifyAttachmentAdapter,
} from './classifyAttachmentAdapter';
import { useAttachmentLimits } from './classifyAttachmentLimits';
import type { ClassifyAttachmentRef } from './classifyUpload';
import { useWorkflowAttachments } from '../../hooks/useWorkflowAttachments';

const useBrowserLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

export type { HtsUiPatch } from './HtsClassificationChat';

// Mirrors pyapi/app/agents/hts_managed_agent/stage.py::Stage — the top-level
// workflow stage returned by POST /session and POST /message. Distinct from
// `latest.stage` (UiStage) inside GET /state, which drives message formatting.
// ERROR is deliberately excluded: it's recoverable (e.g. a content-policy block
// asking the user to rephrase), so it must not permanently disable the composer.
const TERMINAL_WORKFLOW_STAGES = new Set(['FINAL_CLASSIFICATION', 'STOPPED']);

// One line in the turn's accumulating "Thought for Ns" card — mirrors EARChatUI.tsx's
// EarThoughtStep (minus its 'group' level, which classify's linear stage model
// has no use for).
//
// `finding` is a DETERMINATION — the result of a step rather than the activity —
// rendered prominently so the classification path being built up reads at a
// glance ("Chapter 84 — machinery", "8471.30 — portable, under 10 kg") against
// the secondary activity lines around it. Emitted by the backend as
// UiStage.FINDING.
type HtsThoughtStep = {
  text: string;
  level: 'phase' | 'step' | 'finding';
};

// ReactMarkdown's default anchor rendering opens in the SAME tab, which would
// navigate the user away from an in-progress classification (e.g. the
// HTS.gov reference link on the final result). Module-scope so the reference
// is stable across renders, as react-markdown's `components` prop expects.
const MARKDOWN_COMPONENTS: Components = {
  a: ({ node: _node, ...props }) => (
    <a {...props} target='_blank' rel='noopener noreferrer' />
  ),
};

// Shared by both the live (in-progress) and finished "Thought for Ns" cards — renders a
// flat list of steps styled to read as a phase heading + indented steps, mirroring
// EARChatUI.tsx's renderThoughtStepEntries (minus its 'group' level).
//
// Note this does NOT hoist a phase heading above the duration summary the way
// EARChatUI does. Export's cards cover one phase each, so promoting it to a title
// reads naturally; a classify turn routinely spans several phases (intake →
// chapter selection, or graph traversal → final review), and hoisting the last
// one left every line after it visually filed under the *previous* heading.
const renderThoughtStepEntries = (
  steps: HtsThoughtStep[],
  isLive = false,
) => {
  // Only newly-streamed lines fade in; a settled card must not re-animate every
  // time something above it re-renders.
  const liveLineClass = isLive ? ' bite-chatkit-thought-line-in' : '';

  // Every level shares the same bulleted row markup, so the trail reads as one
  // continuous list the way Claude's and ChatGPT's activity trails do. Only the
  // text weight/colour separates them: a phase transition is bold, a finding
  // (determination) is mid-weight, an activity line is secondary. Phases were
  // previously uppercase section headers with divider rules, which chopped one
  // turn into visually separate blocks.
  return steps.map((step, index) => {
    const levelClass =
      step.level === 'phase'
        ? ' bite-chatkit-thought-phase'
        : step.level === 'finding'
        ? ' bite-chatkit-thought-finding'
        : '';
    return (
      <div
        className={`bite-chatkit-thought-step${levelClass}${liveLineClass}`}
        key={index}
      >
        <span aria-hidden='true' />
        <div>{step.text}</div>
      </div>
    );
  });
};

type Props = {
  apiUrl: string; // PyAPI base URL, e.g. http://localhost:8002
  showToolbar?: boolean;
  onThreadChange?: (threadId: string | null) => void;
  onNewChat?: () => void;
  onResponseStart?: () => void;
  onResponseEnd?: () => void;
  onError?: (message: string) => void;
  onLivePatch?: (patch: HtsUiPatch) => void;
  /** Sent once, automatically, as the first message (the Export Workflow's Classify-tool
   *  handoff passes the product description). Optional — absent for every other caller. */
  initialMessage?: string;
  /** Small tag shown on that first user bubble, e.g. "Sent from Export Workflow". */
  initialMessageTag?: string;
};

export type HtsClassificationChatHandle = {
  startNewChat: () => Promise<void>;
  getSaveSnapshot: () => HtsClassificationSaveSnapshot;
};

// Ticket-save snapshot types. Mirrors the shape HtsClassificationChat.tsx's
// OpenAI-ChatKit variant builds (HtsObservedChatMessage / HtsFinalReportSnapshot /
// HtsClassificationSaveSnapshot on the classify-save-ticket-updates branch), adapted
// to this component's polling architecture: there's no discrete per-tool-call
// boundary here, so messages are observed off each new GET /state stream item
// instead of an onClientTool callback.
export type HtsObservedChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  createdAt: string;
  source: 'composer' | 'live_patch' | 'finalize';
};

export type HtsFinalReportSnapshot = {
  version: 1;
  source: 'publish_final_classification';
  threadId: string | null;
  savedAt?: string;
  payload: {
    thread_id?: string | null;
    product_desc?: string | null;
    hts_code?: string | null;
    cmdt_desc?: string | null;
    summary_text?: string | null;
    legal_basis_summary?: string | null;
    confidence_note?: string | null;
    reasoning_trace?: string[];
    facts_relied_on?: string[];
    chapter_number?: string | null;
    chapter_description?: string | null;
  };
  generated: Partial<HtsUiPatch['latest']>;
  streamItem: string;
};

export type HtsClassificationSaveSnapshot = {
  version: 1;
  source: 'hts-classification-managed-agent';
  threadId: string | null;
  capturedAt: string;
  observed: {
    messages: HtsObservedChatMessage[];
    streamItems: string[];
  };
  finalReport: HtsFinalReportSnapshot | null;
};

const buildObservedMessageId = () =>
  `hts-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const SUPERSCRIPT_DIGITS: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
};
const SUBSCRIPT_DIGITS: Record<string, string> = {
  '0': '₀',
  '1': '₁',
  '2': '₂',
  '3': '₃',
  '4': '₄',
  '5': '₅',
  '6': '₆',
  '7': '₇',
  '8': '₈',
  '9': '₉',
};

// Digits-only content maps to real Unicode sub/superscript characters (renders anywhere,
// no markup needed); anything else (rare) just loses the tag rather than showing raw markup.
const convertDigitScript = (
  content: string,
  map: Record<string, string>,
): string =>
  /^\d+$/.test(content)
    ? content
        .split('')
        .map((digit) => map[digit])
        .join('')
    : content;

// HTS graph descriptions (pyapi/app/data/hts_graphs/*.json) use raw HTML tags for officially
// formatted terms — <i>/<il> (italics, e.g. "5-<i>tert</i>-Butyl..." or "<il>10 kg</il>"),
// <u> (underline, used for taxonomic names), <sub>/<sup> (chemical formulas, units), <span>
// (inline styling, no semantic meaning), and <newline></newline> (a literal line break inside
// one field). Claude quotes these verbatim into its responses. react-markdown escapes raw
// HTML by default, so without this conversion the tags render as literal text.
const htmlEmphasisToMarkdown = (text: string): string =>
  text
    .replace(/<newline\s*\/?>\s*(?:<\/newline>)?/gi, '  \n')
    .replace(/<span[^>]*>([\s\S]*?)<\/span>/gi, '$1')
    // <u> (underline) has no markdown equivalent; italicize instead, matching the
    // typographic convention for the taxonomic/species names it's used on in this data.
    .replace(/<(i|il|em|u)>([\s\S]*?)<\/\1>/gi, '*$2*')
    .replace(/<(b|strong)>([\s\S]*?)<\/\1>/gi, '**$2**')
    .replace(/<sup>([\s\S]*?)<\/sup>/gi, (_match, content) =>
      convertDigitScript(content, SUPERSCRIPT_DIGITS),
    )
    .replace(/<sub>([\s\S]*?)<\/sub>/gi, (_match, content) =>
      convertDigitScript(content, SUBSCRIPT_DIGITS),
    );

const MAX_OBSERVED_MESSAGE_LENGTH = 4000;
const MAX_OBSERVED_MESSAGES = 100;
const MAX_REPORT_TEXT_LENGTH = 6000;
const MAX_STREAM_ITEM_LENGTH = 4000;
const MAX_STREAM_ITEMS = 100;
const MAX_ARRAY_ITEMS = 20;
const MAX_ARRAY_ITEM_LENGTH = 1000;

const truncateText = (
  value: unknown,
  maxLength: number,
): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength)}... [truncated]`;
};

const truncateNullableText = (
  value: unknown,
  maxLength: number,
): string | null | undefined => {
  if (value === null) {
    return null;
  }

  return truncateText(value, maxLength);
};

const limitStringArray = (values?: string[]): string[] | undefined => {
  if (!Array.isArray(values)) {
    return undefined;
  }

  return values
    .filter((value): value is string => typeof value === 'string')
    .slice(0, MAX_ARRAY_ITEMS)
    .map((value) => truncateText(value, MAX_ARRAY_ITEM_LENGTH) || '');
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  tag?: string;
  // Names of the files the user attached to this turn, shown as chips on their bubble.
  attachedFilenames?: string[];
  thoughtSeconds?: number | null;
  thoughtSteps?: HtsThoughtStep[];
};

// The UiStages that carry actionable content the user is meant to respond to — a
// follow-up question with options, the proposed/final classification, or a
// terminal error/stop. ONLY these become their own chat bubble; every other
// stage (REQUEST_SAVED, NORMALIZED, CHAPTER_CONFIRMED, GRAPH_PROGRESS,
// REASONING_NOTE, PHASE_START, and anything added later) folds into the turn's
// accumulating "Thought for Ns" card, matching how EARChatUI.tsx's progress
// narration folds into its own "Thinking" card rather than appearing as bubbles.
//
// Deliberately an allowlist of answers rather than of narration: an unknown or
// missing stage then defaults to narration, which degrades gracefully. The
// reverse default is what produced spurious bubbles and split thought cards
// whenever a stage couldn't be determined.
// Interim stages excluded from the reasoning trail ONLY because each already
// publishes a terse UiStage.FINDING carrying the same determination — keeping both
// would duplicate every line. Their own text is the full official tariff
// description (markdown headings plus the inline HTML the HTS text uses), which is
// right for the answer message and the Classification Path panel but renders as
// literal `### HTS Number:` / `<il>10 kg</il>` in a plain-text step row.
//
// Anything NOT listed here still shows — the trail should err toward showing
// progress. In particular REQUEST_SAVED stays (it's the first "we've got it"
// signal of a turn) and CHAPTER_CONFIRMED's confirmation is surfaced as its own
// finding.
const TRAIL_EXCLUDED_STAGES = new Set([
  'NORMALIZED',
  'CHAPTER_CONFIRMED',
  'GRAPH_PROGRESS',
]);

// How long each queued reasoning line waits before surfacing. Slow enough that
// the card visibly fills across a long wait rather than dumping a poll's whole
// batch in one frame; fast enough that a short turn isn't held up (anything still
// queued is flushed the moment the answer arrives — see flushThoughtSteps).
const THOUGHT_REVEAL_MS = 700;

const ANSWER_STAGES = new Set([
  'CHAPTER_SUGGESTED',
  'GRAPH_CLARIFICATION',
  'CLARIFICATION',
  'FINAL_REVIEW',
  'FINAL_CLASSIFICATION',
  'FINAL_CLASSIFICATION_ACCEPTED',
  'STOPPED',
  'ERROR',
]);

type StartSessionResponse = {
  thread_id: string;
  claude_session_id: string;
  workflow_stage: string;
  turn_status?: string;
  turn_id?: string | null;
};

type SendMessageResponse = {
  thread_id: string;
  workflow_stage: string;
  turn_status?: string;
  turn_id?: string | null;
};

type LiveStateResponse = {
  stream_items: string[];
  // One UiStage per stream_item, positionally aligned. `latest` is a single
  // snapshot the backend overwrites on every publish, so when a poll returns
  // several new items at once it only describes the LAST one — this is how the
  // earlier items get attributed to the right stage instead of none.
  stream_stages?: (string | null)[];
  latest: HtsUiPatch['latest'];
  workflow_stage?: string | null;
  turn_status?: string;
  turn_id?: string | null;
  last_error?: string | null;
};

// One file above the box, as in the Design Assistant's composer: a full-width row (icon, bold name, a
// detail on the right, a quiet ×), so any number of files stack instead of crowding the box.
const ComposerFileRow = (props: {
  testId: string;
  fileName: string;
  isImage: boolean;
  detail: string;
  removeDisabled: boolean;
  onRemove: () => void;
}) => (
  <Stack
    direction='row'
    spacing={1}
    alignItems='center'
    data-testid={props.testId}
    data-file-name={props.fileName}
    sx={{
      p: 1,
      borderRadius: 2,
      border: '1px solid var(--es-soft-border, var(--border-color))',
      backgroundColor: 'var(--Background-200, var(--bg-colors))',
    }}
  >
    {props.isImage ? (
      <ImageOutlinedIcon fontSize='small' sx={{ color: 'var(--action-color)' }} />
    ) : (
      <DescriptionOutlinedIcon
        fontSize='small'
        sx={{ color: 'var(--action-color)' }}
      />
    )}
    {/* component='span': Typography renders a <p> by default, and the composer has a rule for <p> (its
        helper text: 12px, tertiary, 500) that would shrink and fade the file name and detail. A span
        takes none of it, so the row keeps the Design Assistant's bold name and secondary-colour size. */}
    <Typography
      component='span'
      sx={{ fontWeight: 700, flex: 1, wordBreak: 'break-word' }}
    >
      {props.fileName}
    </Typography>
    <Typography
      component='span'
      variant='caption'
      sx={{ color: 'var(--text-secondary)' }}
    >
      {props.detail}
    </Typography>
    <IconButton
      aria-label={`Remove ${props.fileName}`}
      onClick={props.onRemove}
      disabled={props.removeDisabled}
      sx={{ width: 28, height: 28, fontSize: '18px' }}
    >
      ×
    </IconButton>
  </Stack>
);

const HtsClassificationChatClaude = (
  {
    apiUrl,
    showToolbar = true,
    onThreadChange,
    onNewChat,
    onResponseStart,
    onResponseEnd,
    onError,
    onLivePatch,
    initialMessage,
    initialMessageTag,
  }: Props,
  ref: React.ForwardedRef<HtsClassificationChatHandle>,
) => {
  const pyApiClient = useMemo(
    () =>
      axios.create({
        baseURL: apiUrl,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    [apiUrl],
  );

  const { cur_user } = useContext(GlobalContext);
  // From the provider this chat is inside, not notistack's shared static `enqueueSnackbar`: that one is
  // rebound to the last provider created, and the profile menu creates (and then destroys) its own, so a
  // toast sent through it is lost once that menu has been opened. An upload refusal then showed nothing.
  const { enqueueSnackbar } = useSnackbar();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [composerValue, setComposerValue] = useState('');
  // What the server allows (files per message, pages and sizes): the defaults until the real numbers arrive.
  const attachmentLimits = useAttachmentLimits(apiUrl);
  // Files chosen in the composer but not yet sent, which upload on Send to Classify's own endpoint, and the
  // uploads still waiting for a turn the server has not taken (so a retry does not upload them again, and
  // the composer shows them where the user can remove them). Import's chat uses the same hook.
  const attachmentAdapter = useMemo(
    () => makeClassifyAttachmentAdapter(apiUrl, attachmentLimits),
    [apiUrl, attachmentLimits],
  );
  const attachments = useWorkflowAttachments(attachmentAdapter, {
    notify: (message, variant) =>
      enqueueSnackbar(message, { variant: variant }),
  });
  const resetAttachments = attachments.reset;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [conversationTitle, setConversationTitle] =
    useState('New Classification');
  const [workflowStage, setWorkflowStage] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thinkingStartedAt, setThinkingStartedAt] = useState<number | null>(
    null,
  );
  const [thinkingElapsedSeconds, setThinkingElapsedSeconds] = useState(0);
  // Bumped whenever thoughtStepsRef gains a new step, forcing a re-render so the live
  // "Thinking" card's contents reflect the latest step immediately (mirrors EARChatUI.tsx's
  // write-only thoughtStepsVersion — the value itself is never read, only set).
  const [, setThoughtStepsVersion] = useState(0);

  const threadIdRef = useRef<string | null>(null);
  // Bumped by "New chat". A turn that was in flight when it was clicked is for a conversation that is gone:
  // its reply, its polling and its failure must not touch the new one (see sendMessage).
  const sessionGenerationRef = useRef(0);
  const lastSeenStreamCountRef = useRef(0);
  // Accumulates every interim/progress line for the CURRENT turn only — reset at the start
  // of each sendMessage call and cleared again once captured onto that turn's finished
  // answer message (see the "settle" effect below). Mirrors EARChatUI.tsx's thoughtStepsRef:
  // progress never becomes its own chat bubble, it grows this ref in place instead.
  const thoughtStepsRef = useRef<HtsThoughtStep[]>([]);
  // Lines that have arrived from the backend but haven't been shown yet — see
  // pushThoughtStep / revealNextThoughtStep for why the reveal is paced.
  const pendingThoughtStepsRef = useRef<HtsThoughtStep[]>([]);
  const thoughtRevealIntervalRef = useRef<number | null>(null);
  const productDescRef = useRef<string | null>(null);
  const observedMessagesRef = useRef<HtsObservedChatMessage[]>([]);
  const observedStreamItemsRef = useRef<string[]>([]);
  const finalReportSnapshotRef = useRef<HtsFinalReportSnapshot | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const chatRootRef = useRef<HTMLDivElement | null>(null);
  const composerFormRef = useRef<HTMLFormElement | null>(null);
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const previousMessageCountRef = useRef(0);
  const wasSendingRef = useRef(false);
  const streamingIntervalRef = useRef<number | null>(null);
  const thinkingStartedAtRef = useRef<number | null>(null);
  const onThreadChangeRef = useRef<Props['onThreadChange']>(onThreadChange);
  const onNewChatRef = useRef<Props['onNewChat']>(onNewChat);
  const onResponseStartRef = useRef<Props['onResponseStart']>(onResponseStart);
  const onResponseEndRef = useRef<Props['onResponseEnd']>(onResponseEnd);
  const onErrorRef = useRef<Props['onError']>(onError);
  const onLivePatchRef = useRef<Props['onLivePatch']>(onLivePatch);

  useEffect(() => {
    onThreadChangeRef.current = onThreadChange;
  }, [onThreadChange]);
  useEffect(() => {
    onNewChatRef.current = onNewChat;
  }, [onNewChat]);
  useEffect(() => {
    onResponseStartRef.current = onResponseStart;
  }, [onResponseStart]);
  useEffect(() => {
    onResponseEndRef.current = onResponseEnd;
  }, [onResponseEnd]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    onLivePatchRef.current = onLivePatch;
  }, [onLivePatch]);

  useBrowserLayoutEffect(() => {
    const input = composerInputRef.current;
    if (!input) return;

    input.style.height = '34px';

    const computedMaxHeight = Number.parseFloat(
      window.getComputedStyle(input).maxHeight,
    );
    const maxHeight = Number.isFinite(computedMaxHeight)
      ? computedMaxHeight
      : 120;
    const nextHeight = Math.min(input.scrollHeight, maxHeight);

    input.style.height = `${Math.max(34, nextHeight)}px`;
    input.style.overflowY =
      input.scrollHeight > maxHeight + 1 ? 'auto' : 'hidden';
  }, [composerValue]);

  useEffect(() => {
    const chatRoot = chatRootRef.current;
    const composerForm = composerFormRef.current;
    if (!chatRoot || !composerForm) return undefined;

    const updateComposerHeight = () => {
      chatRoot.style.setProperty(
        '--bite-hts-composer-height',
        `${Math.ceil(composerForm.getBoundingClientRect().height)}px`,
      );
    };

    updateComposerHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateComposerHeight);
      return () => window.removeEventListener('resize', updateComposerHeight);
    }

    const resizeObserver = new ResizeObserver(updateComposerHeight);
    resizeObserver.observe(composerForm);

    return () => resizeObserver.disconnect();
  }, []);

  const stopStreaming = () => {
    if (streamingIntervalRef.current !== null) {
      window.clearInterval(streamingIntervalRef.current);
      streamingIntervalRef.current = null;
    }
  };

  const stopThoughtReveal = () => {
    if (thoughtRevealIntervalRef.current !== null) {
      window.clearInterval(thoughtRevealIntervalRef.current);
      thoughtRevealIntervalRef.current = null;
    }
  };

  // Drives the composer's finalized/terminal check. Phase headings no longer come
  // from here — the backend emits PHASE_START patches for those (see applyPatch).
  const noteWorkflowStage = useCallback((stage: string | null | undefined) => {
    if (stage) {
      setWorkflowStage(stage);
    }
  }, []);

  useEffect(() => stopStreaming, []);
  useEffect(() => stopThoughtReveal, []);

  // Scroll to the bottom when a genuinely new message is appended, or when a
  // turn that was streaming its final answer into that message just finished
  // — never on every `sending`/`thoughtStepsVersion` update, so a growing
  // live "Thinking" card (which lives in thoughtStepsRef, not `messages`) or
  // an in-progress reply reveal never yanks the view back down while someone
  // reads an earlier message. The `sending` edge catches a long answer that
  // streamed past the bottom of the last scroll with nothing else left to
  // bring it back into view.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const isNewMessage = messages.length !== previousMessageCountRef.current;
    previousMessageCountRef.current = messages.length;

    const justFinishedSending = wasSendingRef.current && !sending;
    wasSendingRef.current = sending;

    if (!isNewMessage && !justFinishedSending) return;

    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  useEffect(() => {
    if (!thinkingStartedAt) {
      setThinkingElapsedSeconds(0);
      return undefined;
    }

    const updateElapsed = () => {
      setThinkingElapsedSeconds(
        Math.max(0, Math.floor((Date.now() - thinkingStartedAt) / 1000)),
      );
    };

    updateElapsed();
    const id = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(id);
  }, [thinkingStartedAt]);

  // Final-answer reveal speed (unchanged from before progress narration streamed too).
  const FINAL_TOKENS_PER_TICK = 3;
  const FINAL_TICK_MS = 18;

  // Progress/interim-note reveal is deliberately slower than the final answer — ~6x
  // less throughput (1 token/40ms vs. 3 tokens/18ms) — so the final classification still
  // reads as the "payoff" moment rather than progress lines arriving at the same clip.
  const PROGRESS_TOKENS_PER_TICK = 1;
  const PROGRESS_TICK_MS = 40;

  const streamInMessageText = useCallback(
    (
      responseId: string,
      text: string,
      speed: 'final' | 'progress' = 'final',
    ) => {
      stopStreaming();
      const tokens = text.match(/\S+|\s+/g) || [text];
      let tokenIndex = 0;
      const tokensPerTick =
        speed === 'progress' ? PROGRESS_TOKENS_PER_TICK : FINAL_TOKENS_PER_TICK;
      const tickMs = speed === 'progress' ? PROGRESS_TICK_MS : FINAL_TICK_MS;

      streamingIntervalRef.current = window.setInterval(() => {
        tokenIndex = Math.min(tokenIndex + tokensPerTick, tokens.length);
        const streamed = tokens.slice(0, tokenIndex).join('');

        setMessages((prev) =>
          prev.map((m) => (m.id === responseId ? { ...m, text: streamed } : m)),
        );

        if (tokenIndex >= tokens.length) {
          stopStreaming();
        }
      }, tickMs);
    },
    [],
  );

  // Queues one line for the current turn's "Thought for Ns" card, de-duped against
  // the last line already queued or shown (mirrors EARChatUI.tsx's thoughtStepsRef
  // push).
  //
  // Queued rather than shown immediately: a 1s poll often returns several new
  // patches at once, which dumped 3-4 lines into the card in a single frame and
  // then sat still for the next several seconds. revealNextThoughtStep below
  // releases them one at a time so the card fills at a readable pace across the
  // wait instead of in bursts.
  const pushThoughtStep = useCallback(
    (text: string, level: HtsThoughtStep['level']) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const queued = pendingThoughtStepsRef.current;
      const last =
        queued.length > 0
          ? queued[queued.length - 1]
          : thoughtStepsRef.current[thoughtStepsRef.current.length - 1];
      if (last?.text === trimmed) return;
      pendingThoughtStepsRef.current = [
        ...queued,
        { text: trimmed, level: level },
      ];
    },
    [],
  );

  // Moves one queued line into the visible card. Driven by an interval while a
  // turn is in flight (see beginThinking).
  const revealNextThoughtStep = useCallback(() => {
    if (pendingThoughtStepsRef.current.length === 0) return;
    const [next, ...rest] = pendingThoughtStepsRef.current;
    pendingThoughtStepsRef.current = rest;
    thoughtStepsRef.current = [...thoughtStepsRef.current, next];
    setThoughtStepsVersion((version) => version + 1);
  }, []);

  // Releases everything still queued at once. Used when the turn's answer lands:
  // the pacing exists to fill a wait, so it must never delay the actual result or
  // drop lines that hadn't surfaced yet.
  const flushThoughtSteps = useCallback(() => {
    if (pendingThoughtStepsRef.current.length === 0) return;
    thoughtStepsRef.current = [
      ...thoughtStepsRef.current,
      ...pendingThoughtStepsRef.current,
    ];
    pendingThoughtStepsRef.current = [];
    setThoughtStepsVersion((version) => version + 1);
  }, []);

  const applyPatch = useCallback(
    (patch: HtsUiPatch, responseId: string) => {
      const text = buildPatchDisplayText(patch);
      if (!text) return;

      const stage = patch.latest?.stage || '';

      // The backend announces each work stage it enters with a PHASE_START patch
      // (UiStage.PHASE_START), which becomes this card's phase heading; every note
      // after it, until the next one, is a step beneath it. The backend owns these
      // boundaries because hts_live_state_store keeps only the most recent
      // `latest`, so the client cannot recover which stage an individual
      // stream_item came from — inferring it from the polled workflow_stage filed
      // whole stages under the wrong heading (chapter work appearing under
      // "Normalizing product details").
      if (stage === 'PHASE_START') {
        pushThoughtStep(text, 'phase');
        return;
      }

      // A terse determination — the substance of the trail.
      if (stage === 'FINDING') {
        pushThoughtStep(text, 'finding');
        return;
      }

      // Superseded by the FINDING published alongside each of these. Their own
      // text is the full official tariff description — markdown headings plus the
      // raw inline HTML (`<il>`, `<sup>`) the HTS text uses — which is right for
      // the answer message and the Classification Path panel but renders as
      // literal `### HTS Number:` / `<il>10 kg</il>` in a plain-text step row.
      // Skipped here only; they still reach onLivePatch, so the right-hand
      // Classification Path is unaffected.
      if (TRAIL_EXCLUDED_STAGES.has(stage)) {
        return;
      }

      // Anything that is not a recognised turn-ending stage folds into the live
      // "Thought for Ns" card instead of becoming its own chat bubble — matching
      // EARChatUI.tsx's progress narration, which never renders as its own bubble
      // either (see docs/classify-streaming-ux-plan.md Phase 0c).
      //
      // Note the test is "is this a KNOWN answer stage?", not "is this a known
      // interim stage?". An unrecognised or missing stage must default to
      // narration: a note mis-filed as a step is cosmetic, whereas a note
      // mis-filed as the turn's answer creates a spurious chat bubble AND ends
      // the thought card early (the settle effect attaches to it), splitting one
      // turn's card in two.
      if (!ANSWER_STAGES.has(stage)) {
        pushThoughtStep(text, 'step');
        return;
      }

      // Turn-ending "real answer": a follow-up question awaiting the user's reply, the
      // final classification, or a terminal error/stop. Exactly one such message exists
      // per turn (this stage is always the last item this turn produces), streamed at
      // the faster "final" pace — the "settle" effect below attaches the accumulated
      // thought steps to it once it exists.
      flushSync(() =>
        setMessages((prev) => {
          const existingIndex = prev.findIndex((m) => m.id === responseId);
          if (existingIndex !== -1) {
            const next = [...prev];
            next[existingIndex] = { ...next[existingIndex], text: '' };
            return next;
          }
          return [
            ...prev,
            { id: responseId, role: 'assistant', text: '' },
          ];
        }),
      );
      streamInMessageText(responseId, text, 'final');
    },
    [pushThoughtStep, streamInMessageText],
  );

  // Settle trigger: once the turn's real-answer message exists in `messages` (created by
  // applyPatch above) and doesn't already carry thoughtSteps, capture whatever accumulated
  // in thoughtStepsRef this turn + the elapsed time onto that message, then clear the refs
  // for the next turn. Mirrors EARChatUI.tsx's own settle effect — keyed on `messages`
  // changing, not on any specific event, since applyPatch doesn't know in advance which
  // patch will turn out to be the turn's last one.
  useEffect(() => {
    if (
      thoughtStepsRef.current.length === 0 &&
      pendingThoughtStepsRef.current.length === 0
    ) {
      return;
    }
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || last.thoughtSteps) return;

    // Include anything still queued: the answer is here, so a paced reveal would
    // only strand lines behind a card that has already stopped growing.
    const capturedSteps = [
      ...thoughtStepsRef.current,
      ...pendingThoughtStepsRef.current,
    ];
    pendingThoughtStepsRef.current = [];
    const capturedSeconds = thinkingStartedAtRef.current
      ? Math.max(1, Math.round((Date.now() - thinkingStartedAtRef.current) / 1000))
      : thinkingElapsedSeconds;
    thoughtStepsRef.current = [];

    const finishedId = last.id;
    setMessages((prev) =>
      prev.map((message) =>
        message.id === finishedId
          ? {
              ...message,
              thoughtSteps: capturedSteps,
              thoughtSeconds: capturedSeconds,
            }
          : message,
      ),
    );
  }, [messages, thinkingElapsedSeconds]);

  const appendObservedMessage = useCallback(
    (message: Omit<HtsObservedChatMessage, 'id' | 'createdAt'>) => {
      observedMessagesRef.current = [
        ...observedMessagesRef.current,
        {
          id: buildObservedMessageId(),
          createdAt: new Date().toISOString(),
          ...message,
          text: truncateText(message.text, MAX_OBSERVED_MESSAGE_LENGTH) || '',
        },
      ].slice(-MAX_OBSERVED_MESSAGES);
    },
    [],
  );

  const resetSaveSnapshot = useCallback(() => {
    productDescRef.current = null;
    observedMessagesRef.current = [];
    observedStreamItemsRef.current = [];
    finalReportSnapshotRef.current = null;
  }, []);

  const buildFinalReportSnapshot = useCallback(
    (
      latest: HtsUiPatch['latest'],
      streamItem: string,
    ): HtsFinalReportSnapshot => ({
      version: 1,
      source: 'publish_final_classification',
      threadId: threadIdRef.current,
      payload: {
        thread_id: threadIdRef.current,
        product_desc: truncateText(
          productDescRef.current,
          MAX_REPORT_TEXT_LENGTH,
        ),
        hts_code: latest?.hts_code,
        cmdt_desc: truncateNullableText(
          latest?.cmdt_desc,
          MAX_REPORT_TEXT_LENGTH,
        ),
        summary_text: truncateNullableText(
          latest?.summary_text,
          MAX_REPORT_TEXT_LENGTH,
        ),
        legal_basis_summary: truncateNullableText(
          latest?.legal_basis_summary,
          MAX_REPORT_TEXT_LENGTH,
        ),
        confidence_note: truncateNullableText(
          latest?.confidence_note,
          MAX_REPORT_TEXT_LENGTH,
        ),
        reasoning_trace: limitStringArray(latest?.reasoning_trace),
        facts_relied_on: limitStringArray(latest?.facts_relied_on),
        chapter_number: latest?.chapter_number,
        chapter_description: truncateNullableText(
          latest?.chapter_description,
          MAX_REPORT_TEXT_LENGTH,
        ),
      },
      generated: latest || {},
      streamItem: truncateText(streamItem, MAX_STREAM_ITEM_LENGTH) || '',
    }),
    [],
  );

  const getSaveSnapshot = useCallback(
    (): HtsClassificationSaveSnapshot => ({
      version: 1,
      source: 'hts-classification-managed-agent',
      threadId: threadIdRef.current,
      capturedAt: new Date().toISOString(),
      observed: {
        messages: [...observedMessagesRef.current],
        streamItems: [...observedStreamItemsRef.current],
      },
      finalReport: finalReportSnapshotRef.current
        ? {
            ...finalReportSnapshotRef.current,
            savedAt: new Date().toISOString(),
          }
        : null,
    }),
    [],
  );

  const processStateResponse = useCallback(
    async (
      threadId: string,
      responseId: string,
      generation?: number,
    ): Promise<LiveStateResponse> => {
      const { data } = await pyApiClient.get<LiveStateResponse>(
        `/classify/hts/managed-agent/state/${threadId}`,
      );
      // The chat was reset while this request was out: none of this belongs in the new one. The caller sees
      // an idle turn, so a poll loop following it ends.
      if (generation !== undefined && sessionGenerationRef.current !== generation) {
        return { stream_items: [], latest: {}, turn_status: 'idle' };
      }

      const streamItems = data?.stream_items || [];
      const latest = data?.latest || {};
      noteWorkflowStage(data?.workflow_stage);
      const startIndex = lastSeenStreamCountRef.current;
      const newItems = streamItems.slice(startIndex);
      const streamStages = data?.stream_stages || [];
      lastSeenStreamCountRef.current = streamItems.length;

      // Only the LAST item in this batch reflects the turn's true final state — `latest` is
      // a single shared snapshot for the whole turn, not per-item. Pairing every earlier item
      // with it would wrongly apply final-stage formatting to intermediate items. Every item
      // shares the turn's one `responseId`: interim items never create a message at all
      // (applyPatch folds them into the thought-steps ref instead), and at most one item per
      // turn is ever a "real answer" (the stage-ending publish always ends the turn), so
      // reusing one id can never collide within a turn in practice.
      //
      // Each item's own stage comes from the positionally-aligned stream_stages, so
      // applyPatch can classify it even though `latest` only describes the last one.
      // Without it a non-last interim note had no stage, failed the "is this
      // narration?" test, and got rendered as the turn's answer bubble.
      newItems.forEach((item, index) => {
        const isLast = index === newItems.length - 1;
        const itemStage = streamStages[startIndex + index] || undefined;
        const patch: HtsUiPatch = {
          stream_item: item,
          latest: isLast
            ? latest
            : // Carry only the stage forward for earlier items — never the shared
              // final-state snapshot, which would misformat them.
              ((itemStage ? { stage: itemStage } : {}) as HtsUiPatch['latest']),
        };
        applyPatch(patch, responseId);
        if (onLivePatchRef.current) {
          onLivePatchRef.current({ stream_item: item, latest: latest });
        }

        observedStreamItemsRef.current = [
          ...observedStreamItemsRef.current,
          truncateText(item, MAX_STREAM_ITEM_LENGTH) || '',
        ].slice(-MAX_STREAM_ITEMS);

        const stage = isLast ? latest.stage || '' : '';
        const isFinal = stage === 'FINAL_CLASSIFICATION';
        appendObservedMessage({
          role: 'assistant',
          text: buildPatchDisplayText(patch) || item,
          source: isFinal ? 'finalize' : 'live_patch',
        });

        if (isFinal) {
          finalReportSnapshotRef.current = buildFinalReportSnapshot(
            latest,
            item,
          );
        }
      });

      return data;
    },
    [
      appendObservedMessage,
      applyPatch,
      buildFinalReportSnapshot,
      noteWorkflowStage,
      pyApiClient,
    ],
  );

  const pollTurnUntilDone = useCallback(
    async (threadId: string, responseId: string, generation?: number) => {
      // The backend turn loop (pyapi/app/agents/hts_managed_agent/orchestrator.py)
      // has no wall-clock timeout by design — it's bounded by tool-call count
      // (up to ~75 round trips with retries), not time, and some individual
      // tool calls (Qdrant lookups) carry their own 60s timeout. A deep
      // multi-turn graph traversal can legitimately still be making progress
      // well past 2 minutes, so this ceiling needs real headroom — otherwise
      // the client gives up and shows the same "Error" state as a genuine
      // backend failure, with no way for the user to tell them apart.
      const POLL_MS = 1000;
      const MAX_POLLS = 300;

      for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
        // After a reset this reads as an idle turn (see processStateResponse), which ends the loop.
        const state = await processStateResponse(threadId, responseId, generation);
        const turnStatus = state.turn_status || 'idle';

        if (turnStatus !== 'running') {
          if (turnStatus === 'error') {
            throw new Error(
              state.last_error || 'There was an error during classification.',
            );
          }
          return;
        }

        await new Promise((resolve) => window.setTimeout(resolve, POLL_MS));
        if (generation !== undefined && sessionGenerationRef.current !== generation) {
          return;
        }
      }

      throw new Error(
        'Classification is taking longer than expected. Please try again.',
      );
    },
    [processStateResponse],
  );

  const beginThinking = () => {
    setSending(true);
    setError(null);
    // Defensive reset: the "settle" effect should already have cleared these once the
    // previous turn's answer message landed, but a fresh turn must never inherit a
    // stale accumulated thought trail regardless.
    thoughtStepsRef.current = [];
    pendingThoughtStepsRef.current = [];
    setThoughtStepsVersion((version) => version + 1);
    stopThoughtReveal();
    thoughtRevealIntervalRef.current = window.setInterval(
      revealNextThoughtStep,
      THOUGHT_REVEAL_MS,
    );
    const startedAt = Date.now();
    thinkingStartedAtRef.current = startedAt;
    setThinkingStartedAt(startedAt);
    if (onResponseStartRef.current) onResponseStartRef.current();
  };

  const endThinking = () => {
    setSending(false);
    setThinkingStartedAt(null);
    thinkingStartedAtRef.current = null;
    // The turn is over, so anything still queued must surface now rather than
    // trickling out (or being dropped) after the answer is already on screen.
    stopThoughtReveal();
    flushThoughtSteps();
    if (onResponseEndRef.current) onResponseEndRef.current();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    attachments.stage(Array.from(event.target.files || []));
    event.target.value = '';
  };

  const sendMessage = async (rawText: string, options?: { tag?: string }) => {
    const typedText = rawText.trim();
    if (
      (!typedText &&
        attachments.stagedFiles.length === 0 &&
        !attachments.hasPendingUploads()) ||
      sending ||
      attachments.isUploading
    ) {
      return;
    }

    // Which conversation this turn is for. If "New chat" is clicked while it is in flight, everything it
    // would do to the chat after an await is dropped (see the checks below).
    const generation = sessionGenerationRef.current;

    // Files upload first, so a failed upload (the user is told why) sends nothing. A turn without files
    // never waits here, so it goes out exactly as it did before.
    let turnAttachments: ClassifyAttachmentRef[] = [];
    if (attachments.stagedFiles.length > 0 || attachments.hasPendingUploads()) {
      const outcome = await attachments.upload(cur_user?.selected_team?.id);
      // Failed (the files are back in the composer), no team, or "New chat" clicked while the uploads were
      // in flight: nothing is sent.
      if (outcome.status !== 'ready') {
        return;
      }
      turnAttachments = outcome.attachments;
    }

    const attachedNames = turnAttachments.map((a) => a.filename);
    // What the backend gets. A turn needs non-empty text (and the first turn's text becomes the product
    // description), so an attachment-only send says what to do with the files.
    const text = typedText || attachmentOnlyMessage(attachedNames);
    // What the user sees in their own bubble.
    const displayText = typedText || attachmentSummary(attachedNames);
    // Sent only with files, so a turn without any is byte-for-byte what it was before.
    const attachmentFields = attachmentTurnFields(
      turnAttachments,
      cur_user?.selected_team?.id,
    );

    setComposerValue('');
    setConversationTitle((currentTitle) =>
      currentTitle === 'New Classification'
        ? buildConversationTitle(displayText)
        : currentTitle,
    );
    setMessages((prev) => [
      ...prev,
      {
        id: `user_${Date.now()}`,
        role: 'user',
        text: displayText,
        ...(attachedNames.length > 0 ? { attachedFilenames: attachedNames } : {}),
        ...(options?.tag ? { tag: options.tag } : {}),
      },
    ]);
    appendObservedMessage({ role: 'user', text: displayText, source: 'composer' });
    beginThinking();
    // The request below waits while the server prepares the files; say so rather than sit silent.
    const preparationNote = attachmentPreparationNote(turnAttachments);
    if (preparationNote) pushThoughtStep(preparationNote, 'step');

    const responseId = `assistant_${Date.now()}`;

    try {
      // A turn that carries files must prove who is asking: the server checks team membership before it
      // reads one. A plain turn sends no token, exactly as before.
      const turnConfig = await attachmentTurnConfig(turnAttachments);
      if (sessionGenerationRef.current !== generation) return;

      if (!threadIdRef.current) {
        if (!productDescRef.current) {
          productDescRef.current = text;
        }

        const { data } = await pyApiClient.post<StartSessionResponse>(
          '/classify/hts/managed-agent/session',
          { product_desc: text, ...attachmentFields },
          turnConfig,
        );
        // Reset while the request was out: the reply is not for this chat. It must not become its thread,
        // take its files, or be followed.
        if (sessionGenerationRef.current !== generation) return;
        // The server has taken the turn, files included.
        attachments.markSent();

        threadIdRef.current = data.thread_id;
        lastSeenStreamCountRef.current = 0;
        noteWorkflowStage(data.workflow_stage);
        if (onThreadChangeRef.current)
          onThreadChangeRef.current(data.thread_id);

        await pollTurnUntilDone(data.thread_id, responseId, generation);
      } else {
        const threadId = threadIdRef.current;
        const { data } = await pyApiClient.post<SendMessageResponse>(
          '/classify/hts/managed-agent/message',
          { thread_id: threadId, message: text, ...attachmentFields },
          turnConfig,
        );
        if (sessionGenerationRef.current !== generation) return;
        // The server has taken the turn, files included.
        attachments.markSent();

        noteWorkflowStage(data.workflow_stage);
        await pollTurnUntilDone(threadId, responseId, generation);
      }
    } catch (err: any) {
      // A turn for a chat that was reset has nothing to say to the new one: not its failure, not its text.
      if (sessionGenerationRef.current !== generation) return;
      // Only what the user typed goes back into the box; files that had uploaded stay pending, so the
      // retry carries them without uploading again.
      setComposerValue(typedText);
      const status = err?.response?.status;
      // A 413 on a turn with files means the files are over a limit (the detail below says which). They
      // are dropped: kept, every retry would be refused the same way, with no way to take one out.
      if (status === 413 && turnAttachments.length > 0) {
        attachments.markSent();
      }
      const detail =
        status === 404
          ? 'This chat session has expired. Please start a new chat.'
          : toErrorMessage(
              err?.response?.data?.detail ||
                err?.response?.data?.message ||
                err?.message,
              'Unable to send message. Please try again.',
            );
      setError(detail);
      if (onErrorRef.current) onErrorRef.current(detail);
    } finally {
      // A reset has already ended this turn (startNewChat), and may have started another since.
      if (sessionGenerationRef.current === generation) endThinking();
    }
  };

  // Auto-send the handed-off first message exactly once (Export Workflow → Classify tool).
  const initialMessageSentRef = useRef(false);
  useEffect(() => {
    if (!initialMessage || initialMessageSentRef.current) {
      return;
    }
    initialMessageSentRef.current = true;
    sendMessage(initialMessage, { tag: initialMessageTag });
  }, [initialMessage]);

  const handleCustomSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    await sendMessage(composerValue);
  };

  const handleComposerKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }
    event.preventDefault();
    handleCustomSubmit();
  };

  const startNewChat = useCallback(async () => {
    // First, before anything is awaited: a turn in flight is for the conversation that is ending. It is
    // stopped here rather than left to finish on its own, so the composer is free for the new chat at
    // once and none of the old turn's reply, polling or failure reaches it.
    sessionGenerationRef.current += 1;
    if (thinkingStartedAtRef.current !== null) endThinking();
    stopStreaming();
    const threadId = threadIdRef.current;
    if (threadId) {
      await pyApiClient
        .post(`/classify/hts/managed-agent/reset/${threadId}`)
        .catch(() => undefined);
    }

    threadIdRef.current = null;
    lastSeenStreamCountRef.current = 0;
    thoughtStepsRef.current = [];
    pendingThoughtStepsRef.current = [];
    stopThoughtReveal();
    resetSaveSnapshot();
    setMessages([]);
    setComposerValue('');
    // A fresh conversation starts with no files: staged ones, ones already uploaded for the old
    // conversation, and the id they were filed under. An upload still in flight drops its turn instead of
    // landing in this cleared chat.
    resetAttachments();
    setConversationTitle('New Classification');
    setWorkflowStage(null);
    setError(null);

    if (onNewChatRef.current) onNewChatRef.current();
    if (onThreadChangeRef.current) onThreadChangeRef.current(null);
  }, [pyApiClient, resetAttachments, resetSaveSnapshot]);

  useImperativeHandle(
    ref,
    () => ({ startNewChat: startNewChat, getSaveSnapshot: getSaveSnapshot }),
    [startNewChat, getSaveSnapshot],
  );

  const isFinalized = workflowStage
    ? TERMINAL_WORKFLOW_STAGES.has(workflowStage)
    : false;
  const composerDisabled = sending || isFinalized || attachments.isUploading;
  // A message is on its way or being worked on (files uploading, or the turn running): the Send button
  // shows a spinner so the chat does not look frozen. Not the same as disabled, which a finished chat is too.
  const isProcessing = sending || attachments.isUploading;
  // Files that uploaded but whose turn was not accepted. Hidden while a turn is being sent (the bubble
  // already shows them, as it always did), and back in the box if that turn fails.
  const shownPendingAttachments = sending ? [] : attachments.pendingAttachments;
  const isEmptyComposerState = messages.length === 0 && !sending;

  return (
    <div className='bite-chatkit-shell'>
      {showToolbar && !isEmptyComposerState && (
        <div className='bite-chatkit-toolbar'>
          <div className='bite-chatkit-status'>
            {error ? (
              <span className='bite-chatkit-status-error'>
                Chat unavailable
              </span>
            ) : sending ? (
              <span className='bite-chatkit-status-thinking'>
                Agent responding…
              </span>
            ) : (
              <span className='bite-chatkit-status-ready'>Chat ready</span>
            )}
          </div>
          <button
            type='button'
            className='bite-chatkit-reset-btn'
            onClick={() => startNewChat()}
          >
            Reset Chat
          </button>
        </div>
      )}

      <div
        ref={chatRootRef}
        className='bite-chatkit-react bite-hts-react-chat'
        data-empty-state={isEmptyComposerState ? 'true' : 'false'}
      >
        <div className='bite-chatkit-threadbar'>
          <div className='bite-chatkit-thread-title'>{conversationTitle}</div>
          <div className='bite-chatkit-thread-actions'>
            <button
              type='button'
              className='bite-chatkit-icon-btn'
              onClick={() => startNewChat()}
              disabled={sending}
              aria-label='Start new conversation'
              title='Start new conversation'
            >
              <EditOutlinedIcon sx={{ fontSize: 21 }} />
            </button>
          </div>
        </div>

        <div className='bite-chatkit-messages' ref={scrollRef}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`bite-chatkit-message bite-chatkit-message-${message.role}${
                message.tag ? ' is-tagged' : ''
              }`}
            >
              {message.role === 'assistant' &&
                message.thoughtSteps &&
                message.thoughtSteps.length > 0 &&
                (() => {
                  const steps = message.thoughtSteps as HtsThoughtStep[];
                  return (
                    <div className='bite-chatkit-thought-block'>
                      <div className='bite-chatkit-thought-summary bite-chatkit-thought-summary-static'>
                        {formatThoughtDuration(message.thoughtSeconds ?? null)}
                      </div>
                      <div className='bite-chatkit-thought-details'>
                        {renderThoughtStepEntries(steps)}
                        <div className='bite-chatkit-thought-step bite-chatkit-thought-step-done'>
                          <span aria-hidden='true' />
                          <div>Done</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              {message.tag && (
                <span className='bite-chatkit-message-tag'>{message.tag}</span>
              )}
              {message.attachedFilenames &&
                message.attachedFilenames.length > 0 && (
                  <Stack
                    direction='row'
                    spacing={0.5}
                    flexWrap='wrap'
                    sx={{ mb: 0.5 }}
                  >
                    {message.attachedFilenames.map((name, index) => (
                      <Stack
                        key={`${name}-${index}`}
                        direction='row'
                        spacing={0.5}
                        alignItems='center'
                        data-testid='classify-message-attachment'
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          border:
                            '1px solid var(--es-soft-border, var(--border-color))',
                          backgroundColor:
                            'var(--Background-200, var(--bg-colors))',
                        }}
                      >
                        <DescriptionOutlinedIcon
                          sx={{ fontSize: 14, color: 'var(--action-color)' }}
                        />
                        <Typography
                          variant='caption'
                          sx={{ wordBreak: 'break-word' }}
                        >
                          {name}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              <div className='bite-chatkit-message-text'>
                <ReactMarkdown components={MARKDOWN_COMPONENTS}>
                  {htmlEmphasisToMarkdown(message.text)}
                </ReactMarkdown>
              </div>
            </div>
          ))}

          {/* Progress narration never renders as its own chat bubble — it folds into this
              one live, accumulating "Thought for Ns" card instead (see applyPatch/
              pushThoughtStep), matching EARChatUI.tsx's "Thinking..." card. It grows in
              place for the whole turn — reappearing between finished lines too, not just
              before the first — and settles into the static card above once the turn's
              real-answer message lands (see the "settle" effect). */}
          <AnimatePresence>
            {sending &&
              (() => {
                const steps = thoughtStepsRef.current;
                return (
                  <motion.div
                    className='bite-chatkit-thought-block'
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                  >
                    <div className='bite-chatkit-thought-summary bite-chatkit-thought-summary-live'>
                      {formatThoughtDuration(thinkingElapsedSeconds)}
                    </div>
                    <div className='bite-chatkit-thought-details'>
                      {renderThoughtStepEntries(steps, true)}
                      <div className='bite-chatkit-thought-step bite-chatkit-thought-step-thinking'>
                        <span aria-hidden='true' />
                        <div>Thinking…</div>
                      </div>
                    </div>
                  </motion.div>
                );
              })()}
          </AnimatePresence>
        </div>

        <form
          ref={composerFormRef}
          className='bite-chatkit-custom-composer'
          onSubmit={handleCustomSubmit}
        >
          {/* Same rows as the Design Assistant's composer: one full-width row per file, so any
              number of files stack above the box instead of crowding it. Files that already
              uploaded (and will go with the next turn) come first, then the ones still to upload. */}
          {(shownPendingAttachments.length > 0 ||
            attachments.stagedFiles.length > 0) && (
            <Stack spacing={1} sx={{ mb: 1 }} data-testid='classify-staged-files'>
              {shownPendingAttachments.map((attachment) => (
                <ComposerFileRow
                  key={attachment.attachment_id}
                  testId='classify-uploaded-file'
                  fileName={attachment.filename}
                  isImage={attachment.kind === 'image'}
                  detail='Uploaded'
                  removeDisabled={attachments.isUploading}
                  onRemove={() => attachments.removePending(attachment)}
                />
              ))}
              {attachments.stagedFiles.map((file, index) => (
                <ComposerFileRow
                  key={`${file.name}-${index}`}
                  testId='classify-staged-file'
                  fileName={file.name}
                  isImage={isSupportedImageFile(file)}
                  detail={formatFileSize(file.size)}
                  // Removing a file mid-upload would not stop its upload, so it waits.
                  removeDisabled={attachments.isUploading}
                  onRemove={() => attachments.remove(index)}
                />
              ))}
            </Stack>
          )}
          <div className='bite-chatkit-custom-input-wrap'>
            <input
              ref={fileInputRef}
              hidden
              type='file'
              multiple
              accept={attachments.accept}
              onChange={handleFileChange}
              data-testid='classify-upload-file-input'
            />
            <button
              type='button'
              className='bite-chatkit-attach'
              aria-label={attachmentHint(attachmentLimits)}
              title={attachmentHint(attachmentLimits)}
              data-testid='classify-composer-attach-button'
              onClick={() => fileInputRef.current?.click()}
              disabled={composerDisabled}
            >
              <AddOutlinedIcon fontSize='small' />
            </button>
            <textarea
              ref={composerInputRef}
              id='hts-product-description'
              className='bite-chatkit-custom-input'
              value={composerValue}
              onChange={(event) => setComposerValue(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              placeholder='Describe your products make up and purpose in detail...'
              aria-label='Describe your product'
              rows={1}
              disabled={composerDisabled}
              autoFocus
            />
            <button
              type='submit'
              className={
                isProcessing
                  ? 'bite-chatkit-custom-send bite-chatkit-custom-send-loading'
                  : 'bite-chatkit-custom-send'
              }
              disabled={
                (!composerValue.trim() &&
                  attachments.stagedFiles.length === 0 &&
                  attachments.pendingAttachments.length === 0) ||
                composerDisabled
              }
              aria-label='Send message'
              aria-busy={isProcessing || undefined}
            >
              {isProcessing ? (
                <CircularProgress size={18} thickness={5} color='inherit' />
              ) : (
                <SendIcon sx={{ fontSize: 18 }} />
              )}
            </button>
          </div>
        </form>
      </div>

      {error && <div className='bite-chatkit-error-banner'>{error}</div>}
    </div>
  );
};

const ForwardedHtsClassificationChatClaude = React.forwardRef(
  HtsClassificationChatClaude,
);
ForwardedHtsClassificationChatClaude.displayName =
  'HtsClassificationChatClaude';

export default ForwardedHtsClassificationChatClaude;
